// Third-party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, Route, Redirect, Switch } from 'react-router-dom';

// Components
import SavedFunds from './partials/SavedFunds';
import SavedBenchmarks from './partials/SavedBenchmarks';
import { Header, OverlappingBackground } from '../../components/layout';
import { SecondaryNavigation } from '../../components/navigation';
import SearchInput from '../../components/inputs/SearchInput';
import { AdvancedSearch } from '../../components/search';
import { ErrorBoundary } from '../../components/errors';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { ADVANCED_SEARCH_OPTIONS } from '../../../utils/constants';
import { userHasPermission, PERMISSIONS } from '../../../utils/permissions';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import Nullable from '@/types/nullable';
import User from '@/types/user';

const SEARCH_TABS = {
  ALL: '/search',
  FUNDS: '/search/funds',
  SAVED_FUNDS: '/search/saved-funds',
  BENCHMARKS: '/search/benchmarks',
  SAVED_BENCHMARKS: '/search/saved-benchmarks',
};

type StateProps = {
  user?: Nullable<User>;
};

type Props = RouteComponentProps & StateProps;

type State = {
  query: string;
};

class Search extends Component<Props, State> {
  readonly state: State = {
    query: '',
  };

  getTabOptions = () => {
    const { user } = this.props;

    const items = [
      {
        value: SEARCH_TABS.ALL,
        label: formatMessage('Search.Nav.All'),
        exact: true,
        subValues: [SEARCH_TABS.BENCHMARKS, SEARCH_TABS.FUNDS],
      },
      {
        value: SEARCH_TABS.SAVED_FUNDS,
        label: formatMessage('Search.Nav.SavedFunds'),
      },
      {
        value: SEARCH_TABS.SAVED_BENCHMARKS,
        label: formatMessage('Search.Nav.SavedBenchmarks'),
        hidden: !user || !userHasPermission(user, PERMISSIONS.BENCHMARKS),
      },
    ];

    return items.filter(i => !i.hidden);
  };

  getSelectedTab = () => this.getTabOptions().find(item => item.value === this.props.location.pathname);

  getSelectedTabValue = () => {
    const tab = this.getSelectedTab();
    return tab ? tab.value : null;
  };

  onSearchChange = (query: string) => {
    this.setState({ query: query || '' });
  };

  getSearchInput = () => {
    const { query } = this.state;

    let placeholder;

    switch (this.getSelectedTabValue()) {
      case SEARCH_TABS.SAVED_FUNDS: {
        placeholder = formatMessage('Search.Funds.SavedPlaceholder');
        break;
      }
      case SEARCH_TABS.SAVED_BENCHMARKS: {
        placeholder = formatMessage('Search.Benchmarks.SavedPlaceholder');
        break;
      }
      default:
        break;
    }
    return placeholder && <SearchInput className="dark search-input" onChange={this.onSearchChange} placeholder={placeholder} value={query} />;
  };

  render() {
    const { query } = this.state;
    const { user } = this.props;

    const hasBenchmarkPermission = !!user && userHasPermission(user, PERMISSIONS.BENCHMARKS);

    return (
      <>
        <Header />
        <OverlappingBackground />

        <main id="content" className="search-page">
          <div className="search-navigation d-flex align-items-center">
            <h1 className="h3">
              <FormattedMessage id="Search.PageTitle" />
            </h1>
            <SecondaryNavigation className="flex-grow-1" items={this.getTabOptions()} />

            {this.getSearchInput()}
          </div>

          <ErrorBoundary>
            <div className="search-content">
              <Switch>
                <Route
                  exact
                  path={`${this.props.match.path}/`}
                  render={(props: RouteComponentProps) => <AdvancedSearch {...props} className="all-items" useWindowScrollAncestor />}
                />

                <Route
                  exact
                  path={`${this.props.match.path}/funds`}
                  render={(props: RouteComponentProps) => (
                    <AdvancedSearch {...props} selectedOption={ADVANCED_SEARCH_OPTIONS.FUNDS} className="all-items" useWindowScrollAncestor />
                  )}
                />

                {hasBenchmarkPermission && (
                  <Route
                    exact
                    path={`${this.props.match.path}/benchmarks`}
                    render={(props: RouteComponentProps) => (
                      <AdvancedSearch {...props} selectedOption={ADVANCED_SEARCH_OPTIONS.BENCHMARKS} className="all-items" useWindowScrollAncestor />
                    )}
                  />
                )}

                <Route path={`${this.props.match.path}/saved-funds`} render={(props: RouteComponentProps) => <SavedFunds {...props} query={query} />} />

                {hasBenchmarkPermission && (
                  <Route
                    path={`${this.props.match.path}/saved-benchmarks`}
                    render={(props: RouteComponentProps) => <SavedBenchmarks {...props} query={query} />}
                  />
                )}

                <Redirect to={this.props.match.path} />
              </Switch>
            </div>
          </ErrorBoundary>
        </main>
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user,
});

export default withRouter(connect(mapStateToProps)(Search));
